import React, { useContext } from 'react';
import { Table, Tag, Typography, Empty } from 'antd';
import { UnorderedListOutlined } from '@ant-design/icons';
import { IInvoice } from '../../../interfaces';
import { EInvoiceStatus } from '../../../enums';
import StatusTag from '../../CityInvoices/components/StatusTag';
import { convertMoedaBRDoJSNovo, formatDateStringToDateAndTime } from '../../../utils/Conversores';
import { SortOrder } from 'antd/es/table/interface';
import { ThemeContext } from '../../../contexts/ThemeContext';

const { Link } = Typography;

interface InvoicesHistoryCardProps {
  invoices?: IInvoice[];
  currentInvoiceId: number;
  onOpenInvoice?: (id: number) => void;
}

export const InvoicesHistoryCard: React.FC<InvoicesHistoryCardProps> = ({
  invoices,
  currentInvoiceId,
  onOpenInvoice,
}) => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === 'dark';

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 110,
      render: (id: number) => (
        <Link
          onClick={() => onOpenInvoice?.(id)}
          style={{ fontWeight: 600, whiteSpace: 'nowrap' }}
        >
          #{id}
        </Link>
      ),
    },
    {
      title: 'Data de Emissão',
      dataIndex: 'date_issue',
      key: 'date_issue',
      defaultSortOrder: 'descend' as keyof SortOrder,
      sorter: (a: IInvoice, b: IInvoice) =>
        new Date(a?.date_issue!).getTime() - new Date(b?.date_issue!).getTime(),
      showSorterTooltip: false,
      render: (data: any) => (data ? formatDateStringToDateAndTime(data) : '--'),
    },
    {
      title: 'Valor Total',
      dataIndex: 'value',
      key: 'value',
      render: (v: number) => (
        <span style={{ fontWeight: 600, color: isDark ? '#52c41a' : '#237804', whiteSpace: 'nowrap' }}>
          {v ? `R$ ${convertMoedaBRDoJSNovo(v)}` : '--'}
        </span>
      ),
    },
    {
      title: 'Tipo',
      dataIndex: 'type',
      key: 'type',
      align: 'center' as const,
      sorter: true,
      showSorterTooltip: false,
      filters: [
        { text: 'NFS', value: 'NFS' },
        { text: 'NFC', value: 'NFC' },
        { text: 'NFE', value: 'NFE' },
      ],
      filterMultiple: true,
      onFilter: (value: any, record: IInvoice) => record.type === value,
      render: (_: any, { type }: IInvoice) => {
        let color = 'blue';
        if (type === 'NFC') color = 'purple';
        if (type === 'NFE') color = 'cyan';

        return (
          <Tag
            color={color}
            style={{ borderRadius: 10, fontWeight: 600, fontSize: 11, margin: 0 }}
          >
            {type ?? '--'}
          </Tag>
        );
      },
      width: 100,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      filters: [
        { text: 'Concluído', value: EInvoiceStatus.completed },
        { text: 'Com erro', value: EInvoiceStatus.error },
        { text: 'Alerta', value: EInvoiceStatus.warning },
        { text: 'Em processamento', value: EInvoiceStatus.processing },
        { text: 'Reprocessando', value: EInvoiceStatus.reprocessing },
      ],
      filterMultiple: false,
      onFilter: (value: any, record: IInvoice) => record.status === value,
      render: (_: any, { id, status, message }: IInvoice) => (
        <StatusTag key={`tag-${id}`} status={status} message={message} />
      ),
      width: 170,
    },
    {
      title: 'Bilhetes',
      dataIndex: 'tickets',
      key: 'tickets',
      align: 'center' as const,
      render: (_: any, { tickets }: IInvoice) => {
        const count = tickets ? tickets.length : 0;
        if (count === 0) {
          return (
            <Tag color="default" style={{ color: '#8c8c8c', borderRadius: 10, margin: 0, fontWeight: 500 }} bordered={false}>
              0 Bilhetes
            </Tag>
          );
        }

        return (
          <Tag color="#1677ff" style={{ borderRadius: 10, margin: 0, fontWeight: 600 }}>
            {count} {count === 1 ? 'Bilhete' : 'Bilhetes'}
          </Tag>
        );
      },
      width: 120,
    },
  ];

  const cardBg = isDark ? '#141414' : '#ffffff';
  const labelColor = isDark ? '#8c8c8c' : '#64748b';
  const textColor = isDark ? '#f0f0f0' : '#1e293b';

  return (
    <div
      style={{
        background: cardBg,
        borderRadius: 14,
        border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
        boxShadow: isDark
          ? '0 4px 20px rgba(0, 0, 0, 0.35)'
          : '0 2px 10px rgba(0, 0, 0, 0.03)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          padding: '16px 20px',
          borderBottom: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12,
          background: isDark ? '#1a1a1a' : '#fafafa',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              background: isDark ? 'rgba(22, 119, 255, 0.15)' : 'rgba(22, 119, 255, 0.1)',
              color: '#1677ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
            }}
          >
            <UnorderedListOutlined />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: textColor }}>
              Notas do Usuário neste Sorteio
            </div>
            <div style={{ fontSize: 12, color: labelColor }}>
              Histórico de todas as notas fiscais importadas pelo usuário durante o período do sorteio
            </div>
          </div>
        </div>

        <Tag color="success" style={{ margin: 0, fontWeight: 600 }}>
          {invoices?.length || 0} nota(s) encontrada(s)
        </Tag>
      </div>

      {invoices?.length === 0 ? (
        <div style={{ padding: '32px 16px' }}>
          <Empty description="Nenhuma nota encontrada para este usuário no período do sorteio" />
        </div>
      ) : (
        <Table
          className={`modern-data-table ${isDark ? 'modern-data-table-dark' : ''}`}
          columns={columns}
          dataSource={invoices}
          rowKey="id"
          size="small"
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          scroll={{ x: true }}
          onRow={(record) => ({
            style: record.id === currentInvoiceId
              ? {
                background: isDark ? 'rgba(250, 173, 20, 0.12)' : '#fffbe6',
              }
              : {},
          })}
          rowClassName={(record) => (record.id === currentInvoiceId ? 'current-invoice-row' : '')}
        />
      )}
    </div>
  );
};

export default InvoicesHistoryCard;

